import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { SITE } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  subject: "",
  message: "",
  gdpr_consent: false,
};

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.gdpr_consent) {
      toast.error(t.contact.form.gdpr_required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        service: form.service || null,
        gdpr_consent: form.gdpr_consent,
      });
      toast.success(t.contact.form.success);
      setForm(initialForm);
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0d0d0d] border-t border-neutral-900 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="overline mb-6">{t.contact.overline}</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-neutral-400 text-base sm:text-lg max-w-md leading-relaxed">
              {t.contact.subtitle}
            </p>

            <div className="mt-12 space-y-px bg-neutral-800">
              <InfoRow
                icon={<Phone className="h-4 w-4 text-yellow-400" />}
                label={t.contact.phone}
                value={SITE.phone}
                href={`tel:${SITE.phoneRaw}`}
                testId="contact-info-phone"
              />
              <InfoRow
                icon={<MessageCircle className="h-4 w-4 text-[#25D366]" />}
                label="WhatsApp"
                value={SITE.phone}
                href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
                  "Bună ziua, vă contactez de pe site-ul dvs. cu privire la o expertiză tehnică."
                )}`}
                testId="contact-info-whatsapp"
              />
              <InfoRow
                icon={<Mail className="h-4 w-4 text-yellow-400" />}
                label={t.contact.email}
                value={SITE.email}
                href={`mailto:${SITE.email}`}
                testId="contact-info-email"
              />
              <InfoRow
                icon={<MapPin className="h-4 w-4 text-yellow-400" />}
                label={t.contact.address}
                value={SITE.address}
                testId="contact-info-address"
              />
              <InfoRow
                icon={<Clock className="h-4 w-4 text-yellow-400" />}
                label={t.contact.hours}
                value={t.contact.hours_value}
                testId="contact-info-hours"
              />
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="col-span-12 lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#0A0A0A] border border-neutral-800 p-6 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label={t.contact.form.name}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  testId="form-input-name"
                />
                <Field
                  label={t.contact.form.email}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  testId="form-input-email"
                />
                <Field
                  label={t.contact.form.phone}
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testId="form-input-phone"
                />
                <SelectField
                  label={t.contact.form.service}
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  options={t.contact.form.services}
                  placeholder={t.contact.form.select_service}
                  testId="form-input-service"
                />
              </div>

              <div className="mt-5">
                <Field
                  label={t.contact.form.subject}
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  testId="form-input-subject"
                />
              </div>

              <div className="mt-5">
                <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  {t.contact.form.message} *
                </label>
                <textarea
                  name="message"
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid="form-input-message"
                  className="w-full bg-transparent border border-neutral-800 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 transition-colors font-sans text-sm resize-none"
                />
              </div>

              <div className="mt-6 flex items-start gap-3 border border-neutral-800 bg-[#0d0d0d] p-4">
                <input
                  type="checkbox"
                  id="gdpr_consent"
                  name="gdpr_consent"
                  checked={form.gdpr_consent}
                  onChange={(e) => setForm((f) => ({ ...f, gdpr_consent: e.target.checked }))}
                  required
                  data-testid="form-gdpr-consent"
                  className="mt-1 h-4 w-4 shrink-0 accent-yellow-500 cursor-pointer"
                />
                <label htmlFor="gdpr_consent" className="text-xs text-neutral-300 leading-relaxed cursor-pointer">
                  {t.contact.form.gdpr_consent}{" "}
                  <a
                    href="/confidentialitate"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 underline decoration-yellow-500/40 underline-offset-2 hover:text-yellow-300"
                    data-testid="link-privacy-policy-form"
                  >
                    {t.footer.privacy}
                  </a>
                  .
                </label>
              </div>

              <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
                {t.contact.form.gdpr_note}{" "}
                <a
                  href="/confidentialitate"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-yellow-400 underline"
                >
                  {t.footer.privacy.toLowerCase()}
                </a>
                .
              </p>

              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="mt-8 inline-flex items-center gap-3 btn-yellow px-6 py-3.5 text-sm font-mono-tech uppercase tracking-[0.15em] disabled:opacity-60"
              >
                {loading ? t.contact.form.submitting : t.contact.form.submit}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const InfoRow = ({ icon, label, value, href, testId }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {label}
        </span>
      </div>
      <div className="font-display text-white text-base sm:text-lg group-hover:text-yellow-400 transition-colors break-words">
        {value}
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      data-testid={testId}
      className="group block bg-[#0A0A0A] p-5 hover:bg-[#111]"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testId} className="group block bg-[#0A0A0A] p-5">
      {inner}
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div>
    <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="w-full bg-transparent border border-neutral-800 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-[#0A0A0A] border border-neutral-800 px-4 py-3 text-white focus:outline-none focus:border-yellow-500 transition-colors text-sm"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);
